import {createContext,useCallback,useContext,useEffect,useRef,useState,type ReactNode} from 'react';
import {apiJSON,PublicApiError,setAccess} from '../../lib/client';
import {queryClient} from '../lib/queryClient';
import {useSiteSettings} from './SiteSettingsContext';
import type {Member} from '../../../../shared/model';

type Context={isAuthenticated:boolean;status:'checking'|'guest'|'authenticated'|'error';user:({customer_id:number}&Member)|null;error:string;revision:number;retry:()=>void;requestLogin:()=>void};
type Session={token:string;member:Member;expiresAt:number};
const AuthContext=createContext<Context>({isAuthenticated:false,status:'checking',user:null,error:'',revision:0,retry:()=>{},requestLogin:()=>{}});
export function useAuth(){return useContext(AuthContext);}

export function AuthProvider({children}:{children:ReactNode}){
 const {settings,ready}=useSiteSettings();
 const [user,setUser]=useState<Context['user']>(null),[status,setStatus]=useState<Context['status']>('checking'),[error,setError]=useState(''),[revision,setRevision]=useState(0);
 const requestRef=useRef<()=>void>(()=>{}),parentOrigin=useRef('');
 const retry=useCallback(()=>requestRef.current(),[]);

 useEffect(()=>{
  if(!ready)return;
  let disposed=false,currentMember:Member|null=null,expiresAt=0;
  let attempt:{nonce:string;redeeming:boolean;deadline:number;controller:AbortController}|null=null;
  let waiting:ReturnType<typeof setTimeout>|undefined,renewal:ReturnType<typeof setTimeout>|undefined,expiry:ReturnType<typeof setTimeout>|undefined,retryTimer:ReturnType<typeof setTimeout>|undefined;
  let suspended=document.visibilityState==='hidden',interrupted=false,startupFailures=0;
  const standalone=settings.localPreview&&window.parent===window;
  const stopWaiting=()=>{clearTimeout(waiting);waiting=undefined;};
  const cancelAttempt=()=>{const previous=attempt;attempt=null;stopWaiting();previous?.controller.abort();};
  const clearMember=()=>{
   setAccess('member',null);setUser(null);queryClient.clear();
   if(currentMember)setRevision(v=>v+1);
   currentMember=null;expiresAt=0;clearTimeout(renewal);clearTimeout(expiry);
  };
  const valid=()=>!!currentMember&&expiresAt>Date.now();
  const fail=(message:string,definitive=false,retryAllowed=true)=>{
   if(disposed)return;
   cancelAttempt();clearTimeout(retryTimer);retryTimer=undefined;
   if(definitive||!valid()){
    clearMember();startupFailures++;
    if(!definitive&&retryAllowed&&startupFailures<3&&!suspended&&document.visibilityState!=='hidden'){
     setStatus('checking');setError('');retryTimer=setTimeout(()=>{retryTimer=undefined;request();},startupFailures*1000);
    }else{setStatus('error');setError(message);}
    return;
   }
   // A transient renewal failure does not discard an unexpired, verified session.
   // Its original deadline remains enforced; no native/financial request is retried.
   setError(message);clearTimeout(renewal);renewal=setTimeout(request,Math.min(10000,Math.max(1000,expiresAt-Date.now()-1000)));
  };
  const accept=(data:Session)=>{
   if(!data.token||!data.member?.id||!data.member?.username||!Number.isFinite(data.expiresAt)||data.expiresAt<=Date.now())throw Error('Üye oturumu doğrulanamadı.');
   const same=currentMember?.id===data.member.id&&currentMember.siteId===data.member.siteId;
   cancelAttempt();clearTimeout(retryTimer);retryTimer=undefined;startupFailures=0;interrupted=false;
   if(!same){clearMember();setRevision(v=>v+1);}
   currentMember=data.member;expiresAt=data.expiresAt;setAccess('member',data.token);
   setUser({...data.member,customer_id:data.member.id});setStatus('authenticated');setError('');
   clearTimeout(renewal);clearTimeout(expiry);
   renewal=setTimeout(request,Math.max(1000,expiresAt-Date.now()-30000));
   expiry=setTimeout(()=>{if(disposed)return;cancelAttempt();clearMember();setStatus('checking');request();},Math.max(1000,expiresAt-Date.now()));
  };
  function request(force=false){
   if(disposed||suspended||document.visibilityState==='hidden'||!standalone&&!parentOrigin.current)return;
   if(attempt&&!force&&attempt.deadline>Date.now())return;
   cancelAttempt();clearTimeout(retryTimer);retryTimer=undefined;
   clearTimeout(renewal);
   const next={nonce:crypto.randomUUID(),redeeming:false,deadline:Date.now()+20000,controller:new AbortController()};attempt=next;
   if(!valid()){clearMember();setStatus('checking');}
   setError('');stopWaiting();
   waiting=setTimeout(()=>{if(attempt===next)fail('Site oturum bağlantısı yanıt vermedi. Bağlantıyı yenileyin.');},20000);
   if(standalone){
    apiJSON<Session>('/api/local/session',{kind:'member'},'member',next.controller.signal).then(data=>{if(!disposed&&attempt===next)accept(data);}).catch(e=>{if(!disposed&&attempt===next)fail(e.message);});
   }else window.parent.postMessage({type:'trwin-extra.ready',nonce:next.nonce},parentOrigin.current);
  }
  const receive=async(event:MessageEvent)=>{
   if(disposed||event.source!==window.parent||event.origin!==parentOrigin.current||!event.data||typeof event.data!=='object')return;
   const data=event.data;
   if(data.type==='trwin-extra.ping'){
    if(document.visibilityState!=='hidden'&&(suspended||attempt&&attempt.deadline<=Date.now())){resume();return;}
    if(attempt&&!attempt.redeeming)window.parent.postMessage({type:'trwin-extra.ready',nonce:attempt.nonce},parentOrigin.current);
    else if(!attempt&&!valid())request();
    return;
   }
   if(data.type==='trwin-extra.logout'){
    cancelAttempt();clearTimeout(retryTimer);retryTimer=undefined;startupFailures=0;interrupted=false;const revoke=currentMember?apiJSON('/api/session/revoke',{}):null;
    clearMember();setStatus('guest');setError('');try{await revoke;}catch{}return;
   }
   if(!attempt||data.nonce!==attempt.nonce)return;
   if(data.type==='trwin-extra.guest'){cancelAttempt();clearTimeout(retryTimer);retryTimer=undefined;clearMember();setStatus('guest');setError('');return;}
   if(data.type==='trwin-extra.error'){fail(typeof data.message==='string'?data.message:'TRWIN oturumu doğrulanamadı. Site bağlantısını yenileyin.',[401,403].includes(data.status),data.status!==429);return;}
   if(data.type!=='trwin-extra.grant'||typeof data.code!=='string'||attempt.redeeming)return;
   const active=attempt;active.redeeming=true;
   try{const session=await apiJSON<Session>('/api/session/redeem',{code:data.code,nonce:data.nonce},'member',active.controller.signal);if(!disposed&&attempt===active)accept(session);}
   catch(e){if(!disposed&&attempt===active)fail(e instanceof Error?e.message:'Oturum açılamadı.',e instanceof PublicApiError&&[401,403].includes(e.status||0));}
  };
  const expired=(event:Event)=>{
   if((event as CustomEvent).detail?.scope!=='member'||disposed)return;
   cancelAttempt();clearMember();setStatus('checking');request();
  };
  const suspend=()=>{if(disposed)return;suspended=true;interrupted=interrupted||!!attempt||!!retryTimer;cancelAttempt();clearTimeout(retryTimer);retryTimer=undefined;};
  const resume=()=>{
   if(disposed||document.visibilityState==='hidden')return;
   const wasSuspended=suspended;suspended=false;
   if(wasSuspended)startupFailures=0;
   if(interrupted||attempt&&attempt.deadline<=Date.now()||!valid()||Date.now()>=expiresAt-30000){interrupted=false;request();}
  };
  const visibility=()=>{if(document.visibilityState==='hidden')suspend();else resume();};
  window.addEventListener('message',receive);window.addEventListener('extra-auth-expired',expired);
  window.addEventListener('pagehide',suspend);window.addEventListener('pageshow',resume);window.addEventListener('focus',resume);document.addEventListener('visibilitychange',visibility);
  requestRef.current=()=>{startupFailures=0;request(true);};parentOrigin.current='';
  if(standalone)request();
  else if(window.parent!==window){
   try{const origin=new URL(document.referrer).origin;if(!settings.allowedParentOrigins.includes(origin))throw Error();parentOrigin.current=origin;request();}
   catch{setStatus('error');setError('Bu sayfayı TRWIN sitesindeki Extra bağlantısından açın.');}
  }else setStatus('guest');
  return()=>{disposed=true;cancelAttempt();clearTimeout(retryTimer);clearTimeout(renewal);clearTimeout(expiry);requestRef.current=()=>{};window.removeEventListener('message',receive);window.removeEventListener('extra-auth-expired',expired);window.removeEventListener('pagehide',suspend);window.removeEventListener('pageshow',resume);window.removeEventListener('focus',resume);document.removeEventListener('visibilitychange',visibility);setAccess('member',null);};
 },[ready,settings.localPreview,settings.allowedParentOrigins]);

 const requestLogin=()=>{if(parentOrigin.current)window.parent.postMessage({type:'trwin-extra.login'},parentOrigin.current);};
 return <AuthContext.Provider value={{isAuthenticated:status==='authenticated',status,user,error,revision,retry,requestLogin}}>{children}</AuthContext.Provider>;
}
